import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import type { MenuItemDto } from '@/types/dashboard/auth';
import { toAdminPath, toIconClass } from '@/utils/dashboard/menu';

const BASE = 'd-flex align-items-center gap-3 w-100 px-3 py-2 rounded-3 border-0 text-decoration-none text-label-md';
const IDLE = 'bg-transparent text-on-surface-variant';
const ACTIVE = 'bg-surface-high text-primary';

function containsPath(item: MenuItemDto, pathname: string): boolean {
  return item.children.some((c) => {
    const p = toAdminPath(c.path);
    return (p && pathname.startsWith(p)) || containsPath(c, pathname);
  });
}

function MenuItem({ item, depth }: { item: MenuItemDto; depth: number }) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(() => containsPath(item, pathname));
  const indent = { paddingLeft: 16 + depth * 20, minHeight: 44 };
  const path = toAdminPath(item.path);

  if (item.children.length > 0) {
    const hasActive = containsPath(item, pathname);
    return (
      <div>
        <button
          type="button"
          className={`${BASE} ${hasActive ? 'text-on-surface' : IDLE} active-press`}
          style={indent}
          onClick={() => setOpen((o) => !o)}
        >
          <i className={`bi ${toIconClass(item.icon)}`} />
          <span className="flex-grow-1 text-start">{item.title}</span>
          <i className={`bi ${open ? 'bi-chevron-down' : 'bi-chevron-right'} small`} />
        </button>
        {open && (
          <div className="d-flex flex-column gap-1 mt-1">
            {item.children.map((c) => (
              <MenuItem key={c.code} item={c} depth={depth + 1} />
            ))}
          </div>
        )}
      </div>
    );
  }

  if (!path) return null;

  return (
    <NavLink
      to={path}
      style={indent}
      className={({ isActive }) => `${BASE} ${isActive ? ACTIVE : IDLE} active-press`}
    >
      <i className={`bi ${toIconClass(item.icon)}`} />
      {item.title}
    </NavLink>
  );
}

export function SidebarMenu({ items }: { items: MenuItemDto[] }) {
  return (
    <nav className="d-flex flex-column gap-1">
      {items.map((i) => (
        <MenuItem key={i.code} item={i} depth={0} />
      ))}
    </nav>
  );
}